import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceDetailModal } from '@/components/Dashboard/AbsenceDetailModal';
import type { AbsenceDetailSelection } from '@/components/Dashboard/AbsencesModal';
import { EmptyState } from '@/components/EmptyState';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { JournalAttendanceCard } from '@/pages/Overview/JournalAttendanceCard';
import { JournalEntriesList } from '@/pages/Overview/JournalEntriesList';
import { JournalEntriesTable } from '@/pages/Overview/JournalEntriesTable';
import { JournalGradesCard } from '@/pages/Overview/JournalGradesCard';
import { JustificationsCard } from '@/pages/Overview/JustificationsCard';
import { RatingsSectionTabs } from '@/pages/Overview/RatingsSectionTabs';
import { useJournalReports } from '@/pages/Overview/hooks/useJournalReports';
import type { JournalId } from '@/types/journal';
import type { NonAttendance, StudyYearPeriod } from '@/types/overview';
import type { EvaluationChartType } from '@/types/ratings';
import {
  getJournalChart,
  getJournalChartTypes,
  hasJournalItemContent,
  isEmptyReport,
} from '@/utils/journal';

const SECTION_TITLE = 'text-title-24 text-foreground md:text-callout-16';

interface Props {
  journalId: JournalId;
  period: StudyYearPeriod;
}

export const JournalDetails: React.FC<Props> = ({ journalId, period }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [selectedChartType, setSelectedChartType] =
    useState<EvaluationChartType>();
  const [selectedAbsence, setSelectedAbsence] =
    useState<AbsenceDetailSelection>();
  const { data: reports, isFetching } = useJournalReports(journalId, period);

  const chartTypes = getJournalChartTypes(reports?.charts);
  const chartType =
    selectedChartType && chartTypes.includes(selectedChartType)
      ? selectedChartType
      : chartTypes[0];
  const chart = chartType ? getJournalChart(reports?.charts, chartType) : null;
  const attendance = reports?.nonAttendancesByType;
  const attendableLessonsCount = attendance?.attendableLessonsCount ?? 0;
  const journalItems = (reports?.journalItems ?? []).filter(
    hasJournalItemContent,
  );

  const handleAbsenceClick = (absence: NonAttendance, date: string) =>
    setSelectedAbsence({ absence, date });

  if (isFetching) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <PageSpinner className="size-[22px] text-subtle-foreground" />
      </div>
    );
  }

  if (!reports || isEmptyReport(reports)) {
    return (
      <EmptyState
        label={t('overview.empty.title')}
        description={t('overview.empty.description')}
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {!!chart && !!chartType && (
        <section className="flex min-w-0 flex-col gap-3">
          <h2 className={SECTION_TITLE}>{t('overview.journals.grades')}</h2>
          <RatingsSectionTabs
            types={chartTypes}
            value={chartType}
            onChange={setSelectedChartType}
          />
          <JournalGradesCard chart={chart} type={chartType} />
        </section>
      )}

      {attendableLessonsCount > 0 && (
        <section className="flex min-w-0 flex-col gap-3">
          <h2 className={SECTION_TITLE}>
            {t('overview.attendance.statistics')}
          </h2>
          <div className="flex flex-col gap-2">
            <JournalAttendanceCard
              attendableLessonsCount={attendableLessonsCount}
              nonAttendancesCount={attendance?.total ?? 0}
            />
            <div className="grid gap-2 md:grid-cols-2 md:gap-3">
              <JustificationsCard
                title={t('overview.attendance.justified')}
                entries={attendance?.justified ?? []}
                onAbsenceClick={handleAbsenceClick}
              />
              <JustificationsCard
                title={t('overview.attendance.nonJustified')}
                entries={attendance?.nonJustified ?? []}
                onAbsenceClick={handleAbsenceClick}
              />
            </div>
          </div>
        </section>
      )}

      {journalItems.length > 0 && (
        <section className="flex min-w-0 flex-col gap-3">
          <h2 className={SECTION_TITLE}>{t('overview.journals.records')}</h2>
          {isMobile && <JournalEntriesList items={journalItems} />}
          {!isMobile && <JournalEntriesTable items={journalItems} />}
        </section>
      )}

      <AbsenceDetailModal
        selection={selectedAbsence}
        onClose={() => setSelectedAbsence(undefined)}
      />
    </div>
  );
};
